import React, { useRef, useState } from 'react';
import moment from 'moment';
import './CompletedSaleRefundModal.css';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Formik } from 'formik';
import XeroRefundLineAllocation from '../../../../component/XeroRefundLineAllocation';
import { prepareXeroRefundValues } from '../../../../helpers/xeroRefundAllocation';
import appointmentApiController from '../../../../services/appointment.service';
import { ClientInvoiceListViewApi, OpenClientInvoiceDetails, OpenStripeRefundCompletedSaleModal } from 'store/slices/clientinvoiceSlice';
import { refundPaymentStoreApi } from 'store/slices/appointmentSlice';
import { refundRequestErrorMessage } from '../../../calendar/refundPaymentMethods';
import { refundSources, refundDestinations, refundPaymentMethodLabel, buildSaleRefundValues } from './completedSaleRefund';
import customToast from 'component/Toastr';

export const InvoiceRefundDialog = ({ invoice, balance, open, close }) => {
 const dispatch=useDispatch();
 const {t}=useTranslation();
 const [error,setError]=useState('');
 const busy=useRef(false);
 const sources=refundSources(invoice);
 const first=sources[0];
 const currency=invoice?.currency || first?.currency || 'AUD';
 const money=value=>new Intl.NumberFormat('en-AU',{style:'currency',currency}).format(value);
 const cart=Array.isArray(invoice?.cart) && invoice.cart.length ? invoice.cart : (invoice?.appointments || []);
 const items=cart.length ? cart : [{name:invoice?.invoice_number ? `Invoice ${invoice.invoice_number}` : t('Invoice'),qty:1}];
 const initialValues={sale_id:invoice?.id,client_id:invoice?.client_id || invoice?.client?.id,deposit_payment_by:first?.payment_method || '',refund_to:first?.payment_method || '',total_deposit_refund:first?.refundable_amount_minor!=null ? (first.refundable_amount_minor/100).toFixed(2) : '',xero_line_allocations:[]};
 const submit=async(values,{setSubmitting})=>{
  if(busy.current) return;
  busy.current=true;setError('');
  try {
   const refundValues=await prepareXeroRefundValues(buildSaleRefundValues(invoice,values,balance),appointmentApiController);
   const action=await dispatch(refundPaymentStoreApi(refundValues));
   if(action.meta.requestStatus!=='fulfilled') throw new Error(refundRequestErrorMessage(action.payload)||t('Refund failed'));
   dispatch(ClientInvoiceListViewApi({client_id:refundValues.client_id}));
   close();
   dispatch(OpenClientInvoiceDetails(''));
   customToast.success(t('Refund Complete'));
  } catch(failure) {setError(failure.message || t('Refund failed'));}
  finally {busy.current=false;setSubmitting(false);}
 };
 if(!open) return null;
 return <>
  <div className="modal fade show" role="dialog" aria-modal="true" aria-labelledby="completed-refund-title" style={{display:'block'}}>
   <div className="modal-dialog modal-lg modal-dialog-centered ac-setup invoice-refund-dialog"><div className="modal-content"><div className="modal-body pb-0">
    <Formik key={`${invoice?.id}:${invoice?.client_id}:${JSON.stringify(sources)}`} initialValues={initialValues} onSubmit={submit}>
     {formik=><form onSubmit={formik.handleSubmit}>
      <div className="p-3">
      <h5 id="completed-refund-title" className="mb-3 fw-semibold text-dark">{t('Issue Refund')}</h5>
      {sources.length > 1 && <div className="mb-3">
      <p>{t('Refund each payment method separately.')}</p>
      <label htmlFor="refund-source">{t('Original payment method')}</label>
      <select id="refund-source" className="form-control mb-3" value={formik.values.deposit_payment_by} disabled={formik.isSubmitting} onChange={event=>{
       const source=sources.find(row=>row.payment_method===event.target.value);
       if (!source) return;
       formik.setValues({...formik.values,deposit_payment_by:source.payment_method,refund_to:source.payment_method,total_deposit_refund:source.refundable_amount_minor!=null ? (source.refundable_amount_minor/100).toFixed(2) : '',xero_line_allocations:[]});setError('');
      }}>
       {!sources.length && <option value="">{t('No refundable payments')}</option>}
       {sources.map(source=><option key={source.payment_method} value={source.payment_method}>{refundPaymentMethodLabel(source.payment_method)} — {source.refundable_amount_minor!=null ? `${t('Available')} ${(source.refundable_amount_minor/100).toFixed(2)}` : `${t('Paid')} ${(source.paid_amount_minor/100).toFixed(2)}`} {source.currency || ''}</option>)}
      </select>
      </div>}
      <div className="table-responsive">
       <table className="table table-borderless mb-4" style={{tableLayout:'fixed'}}>
        <thead className="border-bottom"><tr>{['Item','Quantity','Amount Paid','Refund'].map(label=><th key={label} className="fw-semibold pb-4" scope="col">{t(label)}</th>)}</tr></thead>
        <tbody>{items.map((item,index)=>{
         const appointment=item.appointment || item;
         const staff=appointment.staff;
         const staffName=[staff?.first_name,staff?.last_name].filter(Boolean).join(' ');
         const name=item.name || item.services?.name || item.service?.name || item.products?.name || item.product?.name || item.membership?.name || item.subscription?.name || item.type || t('Invoice');
         const selected=sources.find(source=>source.payment_method===formik.values.deposit_payment_by);
         return <tr key={item.id || index} className="border-bottom">
          <td className="py-4"><h6 className="mb-1">{name}</h6>
           {staffName && <p className="mb-0 small">{t('With')} {staffName}
            {appointment.start_time && appointment.end_time && <> {t('from')} {moment(appointment.start_time,'HH:mm:ss').format('hh:mm A')} - {moment(appointment.end_time,'HH:mm:ss').format('hh:mm A')}</>}
           </p>}
          </td><td className="py-4">{item.qty ?? item.quantity ?? 1}</td>
          {index===0 && <><td className="py-4" rowSpan={items.length}>{selected ? money((selected.paid_amount_minor ?? selected.refundable_amount_minor)/100) : '—'}</td>
          <td className="py-4" rowSpan={items.length}><label htmlFor="refund-amount" className="visually-hidden">{t('Refund amount')}</label>
           <input id="refund-amount" name="total_deposit_refund" className="form-control" type="number" min="0.01" step="0.01" value={formik.values.total_deposit_refund} disabled={formik.isSubmitting} onChange={formik.handleChange}/></td></>}
         </tr>;
        })}</tbody>
        <tfoot><tr><td colSpan="2"/><td className="border-bottom py-4"><h5 className="fw-semibold mb-0">{t('Total')} {currency}</h5></td><td className="border-bottom py-4"><h5 className="mb-0">{money(Number(formik.values.total_deposit_refund)||0)}</h5></td></tr></tfoot>
       </table>
      </div>
      <div className="mb-4" role="group" aria-label={t('Refund to')}>
       <h6 className="fw-semibold mb-3">{t('Choose Refund Payment Type')}</h6>
       <div className="row justify-content-center">
        {refundDestinations(formik.values.deposit_payment_by).map(method=><div className="col-6 col-sm-3 mb-3" key={method}>
         <button type="button" aria-pressed={formik.values.refund_to===method} className={`btn ${formik.values.refund_to===method ? 'btn-dark' : 'btn-light-gray'} w-100`} disabled={formik.isSubmitting} onClick={()=>formik.setFieldValue('refund_to',method)}>{t(refundPaymentMethodLabel(method))}</button>
        </div>)}
       </div>
      </div>
      <XeroRefundLineAllocation active={open} formik={formik}/>
      {error && <p role="alert" className="text-danger">{error}</p>}
      </div>
      <div className="row mt-3 bg-cream p-4 justify-content-center">
       <div className="col-6"><button type="button" className="preview btn me-1 cursor-pointer btn-preview w-100" disabled={formik.isSubmitting} onClick={close}>{t('Cancel')}</button></div>
       <div className="col-6"><button type="submit" className="btn btn-primary w-100" disabled={formik.isSubmitting || !sources.length}>{formik.isSubmitting ? t('Refunding…') : t('Refund Payments')}</button></div>
      </div>
     </form>}
    </Formik>
   </div></div></div>
  </div><div className="modal-backdrop show"/>
 </>;
};
InvoiceRefundDialog.propTypes = {
 invoice: PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  client_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currency: PropTypes.string,
  cart: PropTypes.array,
  appointments: PropTypes.array,
  invoice_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  client: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }),
 }),
 balance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
 open: PropTypes.bool,
 close: PropTypes.func.isRequired,
};
const CompletedSaleRefundModal = () => {
 const dispatch=useDispatch();
 const invoice=useSelector(state=>state.clientinvoice.isCompletedSaleDataForRefund);
 const balance=useSelector(state=>state.clientinvoice.isdepositBalance);
 const open=useSelector(state=>state.clientinvoice.isOpenStripeRefundCompletedSaleModal)==='open';
 return <InvoiceRefundDialog invoice={invoice} balance={balance} open={open} close={()=>dispatch(OpenStripeRefundCompletedSaleModal(""))}/>;
};
export default CompletedSaleRefundModal;
